import { Linkedin, Github, Globe, Link2, MapPin, Mail, Phone, Check, Brain, Sparkles } from "lucide-react";

const profCls = (v) => {
  const s = (v || "").toLowerCase();
  if (s.startsWith("expert")) return "bg-emerald-500/20 text-emerald-200 border-emerald-400/40";
  if (s.startsWith("strong")) return "bg-teal-500/20 text-teal-200 border-teal-400/40";
  if (s.startsWith("work")) return "bg-sky-500/15 text-sky-200 border-sky-400/30";
  return "bg-muted text-muted-foreground border-border";
};

// Categorized skill matrix with proficiency/recency (new-schema profiles), match-aware.
const SkillMatrix = ({ categories, matched }) => (
  <div className="space-y-4">
    {categories.map((cat, ci) => (
      <div key={ci} data-testid={`fp-skillcat-${ci}`}>
        <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-primary/80 mb-2">{cat.category}</div>
        <div className="space-y-1.5">
          {(cat.skills || []).map((s, si) => {
            const name = typeof s === "string" ? s : s.name;
            const prof = typeof s === "object" ? s.proficiency : null;
            const hit = matched && name && matched.has(name.toLowerCase());
            return (
              <div key={si} className="flex items-center gap-3 text-sm" data-match={hit ? "true" : undefined}>
                <span className={`font-medium min-w-[150px] inline-flex items-center gap-1.5 ${hit ? "text-emerald-200" : "text-foreground/90"}`}>{hit && <Check className="h-3 w-3 shrink-0" />}{name}</span>
                {prof && <span className={`text-[10px] px-2 py-0.5 rounded-full border ${profCls(prof)}`}>{prof}</span>}
                {typeof s === "object" && s.recency && <span className="text-[11px] text-muted-foreground">{s.recency}</span>}
              </div>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

const CIChips = ({ items }) => (
  <div className="flex flex-wrap gap-2">{(items || []).map((t, i) => <span key={i} className="px-3 py-1 rounded-full text-[12.5px] bg-primary/10 border border-primary/20 text-foreground/90">{t}</span>)}</div>
);

const Chips = ({ items, matched }) => (
  <div className="flex flex-wrap gap-2">
    {(items || []).map((s, i) => {
      const name = typeof s === "string" ? s : s.name;
      const hit = matched && name && matched.has(name.toLowerCase());
      return (
        <span
          key={i}
          data-match={hit ? "true" : undefined}
          className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium border inline-flex items-center gap-1.5 ${hit ? "bg-emerald-500/20 border-emerald-400/50 text-emerald-200" : "bg-primary/10 border-primary/20 text-foreground/90"}`}
        >
          {hit && <Check className="h-3 w-3 shrink-0" />}
          {name}
        </span>
      );
    })}
  </div>
);

// Monospace, letter-spaced section label with an optional count suffix — mirrors
// the "EXPERIENCE · 2 ROLES" language from the CV-first design system.
const Section = ({ title, count, children, testid }) => (
  <div className="rounded-2xl border border-border bg-card p-6 space-y-4" data-testid={testid}>
    <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
      {title}
      {count != null && <span className="text-primary/70"> · {count}</span>}
    </h3>
    {children}
  </div>
);

// Read-only render of a complete candidate profile — shared by candidate + employer views.
export function FullProfile({ profile: p, matched }) {
  if (!p) return null;
  const links = p.links || {};
  const linkRow = [
    ["linkedin", Linkedin, links.linkedin],
    ["github", Github, links.github],
    ["portfolio", Globe, links.portfolio],
    ["website", Link2, links.website],
  ].filter(([, , v]) => v);

  return (
    <div className="space-y-5" data-testid="full-profile">
      <Section title="Contact" testid="fp-contact">
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-foreground/90">
          {p.contact?.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-muted-foreground" />{p.contact.location}</span>}
          {p.contact?.email && <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-muted-foreground" />{p.contact.email}</span>}
          {p.contact?.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{p.contact.phone}</span>}
        </div>
        {linkRow.length > 0 && (
          <div className="flex flex-wrap gap-3 pt-1">
            {linkRow.map(([k, Icon, v]) => (
              <a key={k} href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"><Icon className="h-3.5 w-3.5" /> {v.replace(/^https?:\/\//, "").replace(/\/$/, "").slice(0, 42)}</a>
            ))}
          </div>
        )}
      </Section>

      {p.summary && <Section title="Summary" testid="fp-summary"><p className="text-sm leading-relaxed text-foreground/80 max-w-[70ch]">{p.summary}</p></Section>}

      {p.skill_categories?.length > 0 ? (
        <Section title="Skill matrix" count={(p.skill_categories || []).reduce((n, c) => n + (c.skills || []).length, 0)} testid="fp-skills">
          <SkillMatrix categories={p.skill_categories} matched={matched} />
        </Section>
      ) : p.skills?.length > 0 ? (
        <Section title="Skills" count={p.skills.length} testid="fp-skills"><Chips items={p.skills} matched={matched} /></Section>
      ) : null}
      {p.tools?.length > 0 && <Section title="Tools & technologies" count={p.tools.length} testid="fp-tools"><Chips items={p.tools} matched={matched} /></Section>}

      {p.experience?.length > 0 && (
        <Section title="Experience" count={`${p.experience.length} ${p.experience.length === 1 ? "role" : "roles"}`} testid="fp-experience">
          <div className="divide-y divide-border/60">
            {p.experience.map((e, i) => (
              <div key={i} className="py-3.5 first:pt-0 last:pb-0">
                <div className="flex items-baseline gap-3">
                  <div className="flex-1 font-semibold text-[15px] text-foreground">{e.title}{e.company ? ` · ${e.company}` : ""}</div>
                  {e.period && <div className="text-[11.5px] text-muted-foreground font-mono shrink-0">{e.period}</div>}
                </div>
                {e.location && <div className="text-xs text-muted-foreground mt-0.5">{e.location}</div>}
                {e.summary && <p className="text-[13px] leading-relaxed text-foreground/70 mt-1.5 max-w-[64ch]">{e.summary}</p>}
                {e.highlights?.length > 0 && (
                  <ul className="mt-2 space-y-1.5">
                    {e.highlights.map((h, hi) => <li key={hi} className="text-[13px] text-foreground/70 flex gap-2 leading-relaxed"><span className="text-primary mt-1.5 h-1 w-1 rounded-full bg-primary shrink-0" /> {h}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {p.projects?.length > 0 && (
        <Section title="Projects" count={p.projects.length} testid="fp-projects">
          <div className="divide-y divide-border/60">
            {p.projects.map((pr, i) => (
              <div key={i} className="py-3.5 first:pt-0 last:pb-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[15px] text-foreground">{pr.name}</span>
                  {pr.link && <a href={pr.link.startsWith("http") ? pr.link : `https://${pr.link}`} target="_blank" rel="noreferrer" className="text-primary hover:text-primary/80"><Link2 className="h-3.5 w-3.5" /></a>}
                </div>
                {pr.description && <p className="text-[13px] text-foreground/70 mt-1 leading-relaxed max-w-[64ch]">{pr.description}</p>}
                {pr.tech?.length > 0 && <div className="mt-2"><Chips items={pr.tech} /></div>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {p.education?.length > 0 && (
        <Section title="Education" count={p.education.length} testid="fp-education">
          <div className="divide-y divide-border/60">
            {p.education.map((e, i) => (
              <div key={i} className="flex justify-between gap-3 text-sm py-3 first:pt-0 last:pb-0">
                <div><div className="font-medium text-foreground">{e.degree}{e.field ? `, ${e.field}` : ""}</div><div className="text-muted-foreground text-xs mt-0.5">{e.institution}</div></div>
                <div className="text-[11.5px] text-muted-foreground font-mono shrink-0">{e.period}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {(p.certifications?.length > 0 || p.languages?.length > 0 || p.awards?.length > 0) && (
        <Section title="Certifications, languages & awards" testid="fp-extras">
          <div className="space-y-4">
            {p.certifications?.length > 0 && (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Certifications</div>
                <div className="space-y-1">
                  {p.certifications.map((c, i) => (
                    <div key={i} className="text-sm text-foreground/90">{typeof c === "string" ? c : `${c.name}${c.issuer ? ` — ${c.issuer}` : ""}${c.year ? ` (${c.year})` : ""}`}</div>
                  ))}
                </div>
              </div>
            )}
            {p.languages?.length > 0 && <div><div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Languages</div><Chips items={p.languages} /></div>}
            {p.awards?.length > 0 && <div><div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Awards</div><Chips items={p.awards} /></div>}
          </div>
        </Section>
      )}
      {p.recruitment_intelligence && Object.keys(p.recruitment_intelligence).length > 0 && (() => {
        const ri = p.recruitment_intelligence;
        const signals = [["Leadership", ri.leadership_experience], ["Presales", ri.presales_experience], ["Implementation", ri.implementation_experience], ["Customer-facing", ri.customer_facing_experience]].filter(([, on]) => on);
        return (
          <Section title="Career intelligence" testid="fp-intelligence">
            <div className="rounded-lg border border-violet-500/30 bg-violet-500/10 p-2.5 flex items-center gap-2 text-[11px] text-violet-200 mb-1"><Brain className="h-3.5 w-3.5 shrink-0" /> AI-inferred from the CV — not stated verbatim.</div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              {ri.primary_profession && <div className="flex justify-between gap-3"><span className="text-muted-foreground">Profession</span><span className="font-medium text-right">{ri.primary_profession}</span></div>}
              {ri.seniority && <div className="flex justify-between gap-3"><span className="text-muted-foreground">Seniority</span><span className="font-medium text-right">{ri.seniority}</span></div>}
              {ri.total_experience && <div className="flex justify-between gap-3"><span className="text-muted-foreground">Experience</span><span className="font-medium text-right">{ri.total_experience}</span></div>}
              {ri.certifications_strength && <div className="flex justify-between gap-3"><span className="text-muted-foreground">Certifications</span><span className="font-medium text-right">{ri.certifications_strength}</span></div>}
            </div>
            {signals.length > 0 && <div className="flex flex-wrap gap-2 pt-1">{signals.map(([l], i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full border bg-emerald-500/15 text-emerald-200 border-emerald-400/40">✓ {l}</span>)}</div>}
            {ri.key_technology_strengths?.length > 0 && <div><div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Key technology strengths</div><CIChips items={ri.key_technology_strengths} /></div>}
            {ri.industries?.length > 0 && <div><div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Industries</div><CIChips items={ri.industries} /></div>}
            {(ri.suggested_job_titles?.length > 0 || ri.suggested_job_families?.length > 0) && <div><div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Suggested roles</div><CIChips items={[...(ri.suggested_job_families || []), ...(ri.suggested_job_titles || [])]} /></div>}
            {ri.search_keywords?.length > 0 && <div><div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5"><Sparkles className="h-3 w-3 text-primary" /> Search keywords</div><div className="flex flex-wrap gap-1.5">{ri.search_keywords.map((t, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-muted text-muted-foreground font-mono">{t}</span>)}</div></div>}
          </Section>
        );
      })()}
    </div>
  );
}
